import { useCallback, useMemo } from "react";

import Color from "color";
import {
  AxisLabelsFormatterContextObject,
  Options,
  SeriesClickEventObject,
  SeriesOptionsType,
  TooltipFormatterContextObject,
} from "highcharts";
import capitalize from "lodash-es/capitalize";
import { IntlShape } from "react-intl";

import DateRangeType from "@mapmycustomers/shared/types/range/DateRange";

import localSettings from "@app/config/LocalSettings";
import { DateRange } from "@app/enum/dashboard/dateRanges";
import getChartPointDateRange from "@app/scene/dashboard/utils/getChartPointDateRange";
import colors from "@app/styles/_colors.module.scss";
import { DefaultDateRangeConfiguration } from "@app/types/dashboard/CardConfiguration";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { doesWeekStartFromMonday, getWeekEnd } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";

import messages from "../messages";

import styles from "./useChartConfig.module.scss";

const useChartConfig = (
  history: Array<{ count: number; date: Date }>,
  previousHistory: Array<{ count: number; date: Date }> | undefined,
  dateRange: DefaultDateRangeConfiguration,
  intl: IntlShape,
  onPointClick?: (range: DateRangeType) => void,
  goal?: number
): Options => {
  const formatXAxisLabel = useCallback(
    (x: number) => {
      switch (dateRange.range) {
        case DateRange.DAY:
          return formatDate(x, "p");
        case DateRange.WEEK:
          return formatDate(x, intl.formatMessage(messages.xAxisLabelForWeek));
        case DateRange.MONTH:
          return formatDate(x, "dd");
        case DateRange.YEAR:
          return formatDate(x, "LLL");
        default:
          return formatDate(x, intl.formatMessage(messages.xAxisLabel));
      }
    },
    [dateRange.range, intl]
  );

  const formatTooltipDate = useCallback(
    (x: number) => {
      switch (dateRange.range) {
        case DateRange.DAY: {
          const hour = formatDate(x, SHOULD_USE_12_HOUR_FORMAT ? "hh" : "HH");
          return `${formatDate(x, "PP")}, ${hour}:00 — ${hour}:59`;
        }
        case DateRange.WEEK:
          return formatDate(x, intl.formatMessage(messages.tooltipLabelForWeek));
        case DateRange.MONTH:
          return formatDate(x, "PPP");
        case DateRange.QUARTER:
          return `${formatDate(x, "P")} — ${formatDate(getWeekEnd(x), "P")}`;
        case DateRange.YEAR:
          return capitalize(formatDate(x, "LLLL"));
      }
    },
    [dateRange.range, intl]
  );

  const allZero = history.every(({ count }) => !count);

  const goalY = goal && history.length ? goal / history.length : undefined;

  return useMemo(() => {
    const categories = history.map(({ date }) => formatXAxisLabel(date.valueOf()));
    const series: SeriesOptionsType[] = [];

    series.push({
      clip: false, // avoid thin horizontal lines
      color: colors.primary100,
      cursor: "pointer",
      data: history.map(({ count, date }) => ({
        x: dateRange.range === DateRange.MONTH ? undefined : date.valueOf(),
        y: count,
      })),
      events: {
        click: function (event: SeriesClickEventObject) {
          const clickedDate = history[event.point.index].date.valueOf();
          onPointClick?.(getChartPointDateRange(clickedDate, dateRange.range));
        },
      },
      fillColor: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, colors.primary050],
          [1, Color(colors.primary050).alpha(0.1).rgb().toString()],
        ],
      },
      name: intl.formatMessage(messages.closedStageDealAmount),
      type: "area",
    });

    if (previousHistory) {
      series.push({
        clip: false, // avoid thin horizontal lines
        color: colors.slate,
        dashStyle: "Dot",
        data: history.map(({ date }, index) => ({
          x: dateRange.range === DateRange.MONTH ? undefined : date.valueOf(),
          // using same date as in current history, but using the value from the previous one
          y: previousHistory[index]?.count ?? 0,
        })),
        name: intl.formatMessage(messages.closedStageDealAmount),
        type: "line",
      });
    }

    if (goalY && history.length) {
      series.push({
        color: colors.green100,
        dashStyle: "Dash",
        data:
          dateRange.range === DateRange.MONTH
            ? Array(categories.length).fill(goalY)
            : [
                { x: history[0].date.valueOf(), y: goalY },
                { x: history.at(-1)!.date.valueOf(), y: goalY },
              ],
        enableMouseTracking: false,
        marker: {
          enabled: false,
        },

        showInLegend: false,
        type: "line",
      });
    }

    return {
      chart: {
        plotShadow: false,
        spacingBottom: 4,
        spacingLeft: 0,
        spacingRight: 0,
      },
      credits: undefined,
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          marker: {
            radius: 2,
          },
        },
      },
      series: [
        ...series,
        // this is an empty dummy series which are used to pull chart
        // to bottom when chart is all zeros
        {
          color: "transparent",
          data: [
            {
              x:
                dateRange.range === DateRange.MONTH || !history.length
                  ? undefined
                  : history[0].date.valueOf(),
              y: 1,
            },
          ],
          enableMouseTracking: false,
          showInLegend: false,
          type: "line",
        },
      ],
      time: {
        timezoneOffset: new Date().getTimezoneOffset(),
      },
      title: {
        text: "",
      },
      tooltip: {
        formatter: function (this: TooltipFormatterContextObject): string {
          return `${formatTooltipDate(history[this.point.index].date.valueOf())}<br/>${
            this.series.name
          }: <b>${intl.formatNumber(this.y, {
            currency: localSettings.getCurrencyCode(),
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: "currency",
          })}</b>`;
        },
      },
      xAxis: {
        categories: dateRange.range === DateRange.MONTH ? categories : undefined,
        className: styles.xAxis,
        labels: {
          formatter:
            dateRange.range === DateRange.MONTH
              ? undefined
              : function (this: AxisLabelsFormatterContextObject<number>): string {
                  return formatXAxisLabel(this.value);
                },
          // show label only for every 3 hours in DAY charts
          step:
            dateRange.range === DateRange.DAY
              ? 3
              : dateRange.range === DateRange.MONTH
              ? 1
              : undefined,
        },
        lineWidth: 0,
        startOfWeek:
          dateRange.range === DateRange.MONTH ? undefined : doesWeekStartFromMonday() ? 1 : 0,
        tickLength: 0,
        tickPositions:
          dateRange.range === DateRange.MONTH
            ? undefined
            : history.map(({ date }) => date.valueOf()),
        type: dateRange.range === DateRange.MONTH ? "category" : "datetime",
      },
      yAxis: {
        allowDecimals: false,
        endOnTick: !!goalY && history.reduce((res, item) => Math.max(res, item.count), 0) < goalY,
        gridLineWidth: 0,
        tickPositions: allZero ? (goalY ? [0, Math.ceil(goalY)] : [0]) : undefined,
        title: {
          text: "",
        },
      },
    };
  }, [
    allZero,
    dateRange.range,
    formatTooltipDate,
    formatXAxisLabel,
    goalY,
    history,
    intl,
    onPointClick,
    previousHistory,
  ]);
};

export default useChartConfig;
